import { useQuery } from '@tanstack/react-query';
import { Activity, Box, AlertOctagon, Boxes, ShieldAlert, Wrench, FlaskConical, Clock3 } from 'lucide-react';
import { apiGet } from '@/shared/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

interface Summary {
  items: number;
  kits: number;
  open_quarantines: number;
  open_recalls: number;
  equipments_blocked: number;
  bi_pending: number;
  kits_expiring_3d: number;
  flow_24h: { phase: string; n: number }[];
}

interface FifoItem {
  id: string;
  barcode: string;
  alias?: string;
  template_name?: string;
  sterile_expires_at: string;
  urgency: 'OK' | 'EXPIRING' | 'EXPIRED';
}

interface MaintenanceItem {
  id: string;
  equipment_id: string;
  equipment_code: string;
  equipment_name: string;
  kind: string;
  next_due_at: string;
  urgency: 'OK' | 'DUE_SOON' | 'OVERDUE';
}

function Kpi({
  icon: Icon, label, value, tone = 'neutral',
}: { icon: typeof Box; label: string; value: number | string; tone?: 'neutral' | 'brand' | 'success' | 'warning' | 'danger' }) {
  const toneStyles: Record<string, string> = {
    neutral: 'text-neutral-700 dark:text-neutral-200',
    brand:   'text-brand-700 dark:text-brand-200',
    success: 'text-success',
    warning: 'text-neutral-900',
    danger:  'text-danger',
  };
  return (
    <Card>
      <CardBody className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-200">
          <Icon size={20} strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
          <p className={`text-2xl font-semibold leading-none ${toneStyles[tone]}`}>{value}</p>
        </div>
      </CardBody>
    </Card>
  );
}

const URGENCY: Record<string, 'success' | 'warning' | 'danger'> = {
  OK: 'success',
  EXPIRING: 'warning',
  DUE_SOON: 'warning',
  EXPIRED: 'danger',
  OVERDUE: 'danger',
};

export function DashboardPage() {
  const summary = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => apiGet<Summary>('/v1/dashboard/summary'),
  });
  const fifo = useQuery({
    queryKey: ['dashboard', 'fifo'],
    queryFn: () => apiGet<{ items: FifoItem[] }>('/v1/dashboard/fifo'),
  });
  const maint = useQuery({
    queryKey: ['dashboard', 'maintenance-due'],
    queryFn: () => apiGet<{ items: MaintenanceItem[] }>('/v1/dashboard/maintenance-due'),
  });

  const s = summary.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Visão geral</h1>
        <p className="text-sm text-neutral-500">Indicadores operacionais em tempo real.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        <Kpi icon={Box}          label="Itens ativos"        value={s?.items ?? '—'} />
        <Kpi icon={Boxes}        label="Kits em arsenal"     value={s?.kits ?? '—'} />
        <Kpi icon={ShieldAlert}  label="Em quarentena"       value={s?.open_quarantines ?? '—'} tone={(s?.open_quarantines ?? 0) > 0 ? 'warning' : 'neutral'} />
        <Kpi icon={AlertOctagon} label="Recalls abertos"     value={s?.open_recalls ?? '—'} tone={(s?.open_recalls ?? 0) > 0 ? 'danger' : 'neutral'} />
        <Kpi icon={Wrench}       label="Equipamentos bloqueados" value={s?.equipments_blocked ?? '—'} tone={(s?.equipments_blocked ?? 0) > 0 ? 'warning' : 'neutral'} />
        <Kpi icon={FlaskConical} label="BIs pendentes leitura"   value={s?.bi_pending ?? '—'} />
        <Kpi icon={Clock3}       label="Kits vencendo (3d)"      value={s?.kits_expiring_3d ?? '—'} tone={(s?.kits_expiring_3d ?? 0) > 0 ? 'warning' : 'neutral'} />
        <Kpi icon={Activity}     label="Eventos 24h"             value={s?.flow_24h?.reduce((a, b) => a + b.n, 0) ?? '—'} tone="brand" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Arsenal — próximos a vencer (FIFO)" subtitle="Top 50 kits estéreis" />
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500 dark:bg-neutral-700">
                <tr>
                  <th className="px-4 py-2">Kit</th>
                  <th className="px-4 py-2">Validade</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(fifo.data?.items ?? []).map((k) => (
                  <tr key={k.id} className="border-t border-neutral-200 dark:border-neutral-700">
                    <td className="px-4 py-2">
                      <div className="font-medium">{k.alias ?? k.template_name ?? '—'}</div>
                      <div className="font-mono text-xxs text-neutral-500">{k.barcode}</div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{k.sterile_expires_at}</td>
                    <td className="px-4 py-2"><Badge tone={URGENCY[k.urgency] ?? 'neutral'}>{k.urgency}</Badge></td>
                  </tr>
                ))}
                {!fifo.isLoading && (fifo.data?.items?.length ?? 0) === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-xs text-neutral-500">Nenhum kit em arsenal.</td></tr>
                ) : null}
              </tbody>
            </table>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Manutenções próximas" subtitle="Top 50 equipamentos" />
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500 dark:bg-neutral-700">
                <tr>
                  <th className="px-4 py-2">Equipamento</th>
                  <th className="px-4 py-2">Tipo</th>
                  <th className="px-4 py-2">Quando</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(maint.data?.items ?? []).map((m) => (
                  <tr key={m.id} className="border-t border-neutral-200 dark:border-neutral-700">
                    <td className="px-4 py-2">
                      <div className="font-medium">{m.equipment_name}</div>
                      <div className="font-mono text-xxs text-neutral-500">{m.equipment_code}</div>
                    </td>
                    <td className="px-4 py-2 text-xs">{m.kind}</td>
                    <td className="px-4 py-2 font-mono text-xs">{m.next_due_at}</td>
                    <td className="px-4 py-2"><Badge tone={URGENCY[m.urgency] ?? 'neutral'}>{m.urgency}</Badge></td>
                  </tr>
                ))}
                {!maint.isLoading && (maint.data?.items?.length ?? 0) === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-xs text-neutral-500">Sem manutenções pendentes.</td></tr>
                ) : null}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
